import { useState, useEffect } from 'react';
import { AppBar, Toolbar, Button, IconButton, Tooltip, Typography, Box } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import Logo from './Logo';
import { Link, useLocation } from 'react-router-dom';
import SignModal from './SignModal';
import AuthButtonsSkeleton from './AuthButtonsSkeleton';
import { useUser } from '../../contexts/UserContext';
import CartIconButton from '../cart/CartIconButton';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import LocationPickerModal from './LocationPickerModal';
import { EVENT_CARD_WIDTH, narrowViewportMediaQuery } from '../../constants/layout';

const navAuthButtonSx = {
    borderColor: '#1a1a1a',
    color: '#1a1a1a',
    px: { xs: 1.5, sm: 2, md: 3 },
    py: { xs: 0.5, sm: 0.75, md: 1 },
    fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '1rem' },
    minWidth: 0,
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '8px',
    borderWidth: '2px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '&:hover': {
        borderWidth: '2px',
        borderColor: '#1a1a1a',
        backgroundColor: 'rgba(26, 26, 26, 0.04)',
    },
    transition: 'all 0.2s ease',
    [narrowViewportMediaQuery]: {
        px: 0.75,
        py: 0.35,
        fontSize: '0.7rem',
        borderWidth: '1px',
        '&:hover': {
            borderWidth: '1px',
        },
    },
} as const;

const NavigationBar = () => {
    const { user, isUserLoaded } = useUser();
    const { handleLoginClick, logoutUser, isUserDataLoading } = useAuth();
    const isAuthStateLoading = !isUserLoaded || isUserDataLoading;
    const { companyName } = usePublicSettings();
    const location = useLocation();
    const isHomePage = location.pathname === '/';
    const [isVisible, setIsVisible] = useState(!isHomePage); // Always visible on non-home pages
    const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);

    useEffect(() => {
        // Reset visibility based on page
        if (!isHomePage) {
            setIsVisible(true);
            return;
        }

        // On home page, check initial scroll position
        setIsVisible(window.scrollY > 200);

        const handleScroll = () => {
            // Show navbar after scrolling down 200px on home page
            const scrollPosition = window.scrollY;
            setIsVisible(scrollPosition > 200);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isHomePage]);

    return (
        <AppBar 
            position="fixed" 
            elevation={0}
            sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                color: '#1a1a1a',
                zIndex: 1100,
                transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
                transition: 'transform 0.3s ease-in-out',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            <Toolbar
                sx={{
                    py: 1,
                    px: { xs: 1, sm: 2 },
                    gap: 1,
                    minWidth: 0,
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    [narrowViewportMediaQuery]: {
                        minWidth: `min(100%, ${EVENT_CARD_WIDTH}px)`,
                    },
                }}
            >
                <Link
                    to="/"
                    style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                    className="navbar-logo-link"
                >
                    <Logo />
                </Link>
                {companyName && (
                    <Box className="hide-mobile" sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                            className='fw-500 hide-mobile' 
                            variant="h6"
                            sx={{ color: '#1a1a1a', lineHeight: 1.2 }}
                        >
                            {companyName}
                        </Typography>
                        <Typography
                            variant="caption"
                            className='hide-mobile'
                            sx={{
                                color: '#666',
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                lineHeight: 1.2,
                                mt: 0.25,
                            }}
                        >
                            Discover Your Childs Next Amazing Camp Experience!
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.5, sm: 1 },
                        marginLeft: 'auto',
                        marginRight: { xs: 1.5, sm: '32px' },
                        flexShrink: 0,
                        justifyContent: 'flex-end',
                        [narrowViewportMediaQuery]: {
                            gap: 0.25,
                        },
                    }}
                >
                    {isAuthStateLoading && (
                        <AuthButtonsSkeleton variant="dark" />
                    )}
                    {!isAuthStateLoading && !user && (
                        <>
                            <Button 
                                component={Link}
                                to="/signup"
                                variant="outlined"
                                sx={navAuthButtonSx}
                            >
                                Sign Up
                            </Button>
                            <Button 
                                onClick={handleLoginClick}
                                variant="outlined"
                                sx={navAuthButtonSx}
                            >
                                Login
                            </Button>
                        </>
                    )}
                    {!isAuthStateLoading && user && (
                        <>
                            <Button 
                                component={Link}
                                to="/profile"
                                variant="outlined"
                                sx={{
                                    ...navAuthButtonSx,
                                    [narrowViewportMediaQuery]: {
                                        px: 0.5,
                                        py: 0.35,
                                        fontSize: '0.65rem',
                                    },
                                }}
                            >
                                Family Profiles
                            </Button>
                            <Tooltip title="Choose Location" arrow>
                                <IconButton
                                    onClick={() => setIsLocationPickerOpen(true)}
                                    sx={{
                                        color: '#1a1a1a',
                                        '&:hover': {
                                            backgroundColor: 'rgba(26, 26, 26, 0.04)',
                                        },
                                    }}
                                >
                                    <MapIcon />
                                </IconButton>
                            </Tooltip>
                            <CartIconButton />
                            <Tooltip title="Logout" arrow>
                                <IconButton 
                                    onClick={logoutUser}
                                    sx={{ 
                                        ml: 1, 
                                        color: '#1a1a1a',
                                        '&:hover': {
                                            backgroundColor: 'rgba(26, 26, 26, 0.04)',
                                        },
                                    }}
                                >
                                    <LogoutIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </Toolbar>

            <SignModal />
            <LocationPickerModal open={isLocationPickerOpen} onClose={() => setIsLocationPickerOpen(false)} />
        </AppBar>
    );
};

export default NavigationBar;
